import { forwardRef, PropsWithChildren } from 'react'

import { css, Global, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { createPortal } from 'react-dom'

import { ModalActionProvider } from './ModalActionProvider'
import { ModalActions } from './ModalActions'

const BodyOverflowHidden = css`
  body {
    overflow: hidden;
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const sheetEnter = keyframes`
  0% {
    transform: translateY(100%);
    border-top-left-radius: 1.5rem;
    border-top-right-radius: 1.5rem;
  }
  90% {
    transform: translateY(0);
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
  }
  100% {
    transform: translateY(0);
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`

const Backdrop = styled.div`
  z-index: 99998;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 50%);
  animation: ${fadeIn} 0.3s ease-out;
`

const IOS_SHEET_CUBIC_BEZIER = 'cubic-bezier(0.32, 0.72, 0, 1)'
const FullscreenContainer = styled.div`
  height: 100dvh;
  z-index: 99999;
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.arcadeColors.component.modalBackground};
  overflow: hidden;
  padding: 0;

  animation: ${sheetEnter} 0.3s ${IOS_SHEET_CUBIC_BEZIER} forwards;
`

export const MobileSheetHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

export interface MobileSheetProps {
  onClose?: () => void
  onBack?: () => void
  closable?: boolean
}

/**
 * A WIP fullscreen sheet component for mobile web experiences.
 *
 * @remarks
 * This component is currently in development to iterate on mobile web flows,
 * specifically Card Studio. It is not intended for general use.
 */
export const MobileSheet = forwardRef<HTMLDivElement, PropsWithChildren<MobileSheetProps>>(
  ({ children, onClose, onBack, closable = true }, ref) => {
    return createPortal(
      <>
        <Global styles={BodyOverflowHidden} />
        <Backdrop />
        <FullscreenContainer role="dialog" aria-modal="true" data-testid="mobile-modal" ref={ref}>
          <ModalActionProvider>
            {closable ? <ModalActions onClose={onClose} onBack={onBack} /> : null}
            <Content>{children}</Content>
          </ModalActionProvider>
        </FullscreenContainer>
      </>,
      document.getElementById('modal-root') as HTMLElement
    )
  }
)

MobileSheet.displayName = 'MobileSheet'
